import { motion } from "framer-motion";
import { Search, ShoppingCart, RotateCcw, Clock, Star, Plus, Minus, Check } from "lucide-react";
import { IMG, BLUEFIG_VENDOR_URL } from "@/config";
import { SectionHeading } from "@/components/Section";
import { PortalButton } from "@/components/PortalButton";
import { Reveal, stagger, fadeUp } from "@/components/Reveal";

const PRODUCTS = [
  { name: "Basmati Rice", brand: "Premium Import", size: "20 kg bag", pack: "1 / case", price: "Your price", img: IMG.rice, fav: true },
  { name: "Garam Masala", brand: "House Blend", size: "5 kg pack", pack: "4 / case", price: "Your price", img: IMG.spices, fav: false },
  { name: "Takeout Containers", brand: "EcoPack", size: "500 units", pack: "1 / case", price: "Your price", img: IMG.boxes, fav: true },
  { name: "Canola Oil", brand: "GoldPress", size: "16 L jug", pack: "2 / case", price: "Your price", img: IMG.groceries, fav: false },
];

const BENEFITS = [
  "Order 24/7 from any device", "Quickly reorder previous purchases", "Save frequently ordered products",
  "Access customer-specific pricing", "Track order status", "Digital order confirmations",
];

export default function OrderingShowcase() {
  return (
    <section data-testid="ordering-showcase" className="py-24 md:py-32 bg-butter relative overflow-hidden">
      <div className="max-w-[1440px] mx-auto px-5 md:px-10">
        <div className="grid lg:grid-cols-[1fr_1.15fr] gap-14 items-center">
          {/* Left copy */}
          <div>
            <SectionHeading
              chapter="04 / Online Ordering"
              overline="The Ordering Platform"
              title={<>Order restaurant supplies without the back-and-forth</>}
              lead="Browse available products, view your pricing, select quantities, choose a delivery date and submit your order from your phone or computer."
            />
            <motion.ul variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }} className="grid sm:grid-cols-2 gap-3 mt-8">
              {BENEFITS.map((b) => (
                <motion.li key={b} variants={fadeUp} className="flex items-center gap-2.5 text-sm text-forest/85">
                  <Check className="h-4 w-4 text-forest shrink-0" /> {b}
                </motion.li>
              ))}
            </motion.ul>
            <div className="mt-9">
              <PortalButton variant="solid" testId="ordering-access-btn">Request Online Ordering Access</PortalButton>
            </div>
          </div>

          {/* Right browser mockup */}
          <Reveal delay={0.1}>
            <div className="rounded-2xl bg-white border border-forest/20 shadow-hard-lg overflow-hidden">
              {/* browser bar */}
              <div className="flex items-center gap-2 px-4 py-3 border-b border-forest/10 bg-cream">
                <span className="h-3 w-3 rounded-full bg-peach" />
                <span className="h-3 w-3 rounded-full bg-butter-deep" />
                <span className="h-3 w-3 rounded-full bg-sage-deep" />
                <div className="ml-3 flex-1 rounded-md bg-white border border-forest/10 px-3 py-1 text-[11px] text-forest/50 font-mono-accent">
                  {BLUEFIG_VENDOR_URL.replace("https://", "")}
                </div>
              </div>
              {/* app body */}
              <div className="p-4 md:p-5">
                <div className="flex items-center gap-2 rounded-xl border border-forest/15 px-3 py-2.5 mb-3">
                  <Search className="h-4 w-4 text-forest/50" />
                  <span className="text-sm text-forest/50">Search products, SKU or brand…</span>
                </div>
                <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                  {["All", "Rice & Flour", "Spices", "Packaging", "Oils"].map((c, i) => (
                    <span key={c} className={`whitespace-nowrap text-xs rounded-full px-3 py-1.5 border ${i === 0 ? "bg-forest text-butter border-forest" : "border-forest/20 text-forest/70"}`}>{c}</span>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {PRODUCTS.map((p) => (
                    <div key={p.name} className="rounded-xl border border-forest/12 overflow-hidden">
                      <div className="relative h-20">
                        <img src={p.img} alt={p.name} className="w-full h-full object-cover" />
                        {p.fav && <Star className="absolute top-1.5 right-1.5 h-4 w-4 text-butter-deep fill-butter-deep" />}
                      </div>
                      <div className="p-2.5">
                        <p className="text-[13px] font-semibold text-forest leading-tight">{p.name}</p>
                        <p className="text-[10px] text-forest/50 font-mono-accent">{p.size} · {p.pack}</p>
                        <div className="flex items-center justify-between mt-2">
                          <div className="flex items-center gap-1.5">
                            <span className="grid place-items-center h-5 w-5 rounded bg-cream border border-forest/15"><Minus className="h-3 w-3 text-forest" /></span>
                            <span className="text-xs font-semibold w-4 text-center">2</span>
                            <span className="grid place-items-center h-5 w-5 rounded bg-cream border border-forest/15"><Plus className="h-3 w-3 text-forest" /></span>
                          </div>
                          <span className="grid place-items-center h-6 w-6 rounded-md bg-forest text-butter"><ShoppingCart className="h-3.5 w-3.5" /></span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
                <div className="flex items-center justify-between mt-4 rounded-xl bg-sage/50 border border-forest/10 px-4 py-3">
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-forest"><RotateCcw className="h-3.5 w-3.5" /> Reorder last week</span>
                  <span className="inline-flex items-center gap-2 text-xs font-medium text-forest"><Clock className="h-3.5 w-3.5" /> Deliver: Thu, next day</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
